import React, { useState, useRef, useEffect } from 'react';
import { Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import type { ChatInputProps } from '../types';
import { CHAT_CONFIG } from '../constants';

/**
 * Chat message input component with auto-resize and send functionality
 */
export const ChatInput: React.FC<ChatInputProps> = ({
  value,
  onChange,
  onSend,
  disabled = false,
  placeholder = 'Type your message...',
  className,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [isFocused, setIsFocused] = useState(false);

  // Auto-resize textarea based on content
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = 'auto';
      
      // Set height based on scrollHeight, with min and max constraints
      const newHeight = Math.min(
        Math.max(textarea.scrollHeight, 40), // Min height: 40px
        120 // Max height: 120px (about 4 lines)
      );
      
      textarea.style.height = `${newHeight}px`;
    }
  }, [value]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Send on Enter (but not Shift+Enter)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSend = () => {
    if (disabled || !value.trim()) {
      return;
    }

    if (value.trim().length > CHAT_CONFIG.MAX_MESSAGE_LENGTH) {
      return;
    }

    onSend();
  };

  const remainingChars = CHAT_CONFIG.MAX_MESSAGE_LENGTH - value.length;
  const isNearLimit = remainingChars < 50;
  const isOverLimit = remainingChars < 0;

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {/* Character count indicator */}
      {(isNearLimit || isOverLimit) && (
        <div className="flex justify-end">
          <span
            className={cn(
              'text-xs',
              isOverLimit ? 'text-destructive' : 'text-muted-foreground'
            )}
          >
            {remainingChars} characters remaining
          </span>
        </div>
      )}

      {/* Input area */}
      <div
        className={cn(
          'flex items-end gap-2 p-3 border rounded-lg bg-background',
          isFocused && 'ring-2 ring-ring ring-offset-2',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
      >
        {/* Message textarea */}
        <Textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={placeholder}
          disabled={disabled}
          className={cn(
            'min-h-[40px] max-h-[120px] resize-none border-0 p-0 focus-visible:ring-0 focus-visible:ring-offset-0',
            'scrollbar-thin scrollbar-thumb-muted scrollbar-track-transparent'
          )}
          style={{
            height: '40px', // Initial height
          }}
        />

        {/* Send button */}
        <Button
          onClick={handleSend}
          disabled={disabled || !value.trim() || isOverLimit}
          size="sm"
          className="h-8 w-8 p-0 shrink-0"
          aria-label="Send message"
        >
          <Send className="h-4 w-4" />
        </Button>
      </div>

      {/* Help text */}
      <div className="flex justify-between items-center text-xs text-muted-foreground">
        <span>Press Enter to send, Shift+Enter for new line</span>
        {!isNearLimit && !isOverLimit && (
          <span>{CHAT_CONFIG.MAX_MESSAGE_LENGTH} character limit</span>
        )}
      </div>
    </div>
  );
};
